import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Wrench, Inbox } from "lucide-react";
import { Input } from "../components/ui/input";
import { JobDetail } from "../components/tech/JobDetail";
import { fmtDateOnly, fmtTime12 } from "../components/admin/leadUtils";
import { NotificationBell } from "../components/NotificationBell";
import { PushSettings } from "../components/PushSettings";
import { NotifyPrefsMatrix } from "../components/NotifyPrefs";

const TECH_PREF_CATEGORIES = [
  ["booking", "New job assignments"],
  ["schedule_changes", "Schedule changes & cancellations"],
  ["reminders", "Upcoming appointment reminders"],
  ["messages", "Customer messages"],
  ["estimates", "Estimate approvals & declines"],
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-11 rounded-sm placeholder:text-zinc-600";

const jobStatusColor = {
  Scheduled: "bg-blue-600/20 text-blue-300 border-blue-500/40",
  Confirmed: "bg-emerald-600/20 text-emerald-300 border-emerald-500/40",
  "En Route": "bg-cyan-600/20 text-cyan-300 border-cyan-500/40",
  "On Site": "bg-amber-600/20 text-amber-300 border-amber-500/40",
  "Waiting on Parts": "bg-orange-600/20 text-orange-300 border-orange-500/40",
  Completed: "bg-green-600/20 text-green-300 border-green-500/40",
  Cancelled: "bg-zinc-600/20 text-zinc-400 border-zinc-500/40",
  Rescheduled: "bg-purple-600/20 text-purple-300 border-purple-500/40",
  "No-Show": "bg-red-600/20 text-red-300 border-red-500/40",
};

const TechLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/tech/login`, { email, password });
      onLogin(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto px-6 py-16" data-testid="tech-login">
      <div className="flex items-center gap-2 mb-2">
        <Wrench className="h-6 w-6 text-blue-500" />
        <h1 className="font-heading text-2xl font-bold text-white">Tech Dashboard</h1>
      </div>
      <p className="text-zinc-500 text-sm mb-8">Sign in to see your assigned jobs.</p>
      <form onSubmit={submit} className="space-y-4 bg-[#121212] border border-zinc-800 rounded-xl p-6">
        <Input data-testid="tech-login-email" type="email" required className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input data-testid="tech-login-password" type="password" required className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button data-testid="tech-login-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm py-3 font-semibold">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
        </button>
      </form>
    </div>
  );
};

const TextJobs = ({ onSaved }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    axios.get(`${API}/tech/text-jobs`).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (id) => {
    const f = form[id] || {};
    setForm((p) => ({ ...p, [id]: { ...f, saving: true } }));
    try {
      await axios.post(`${API}/tech/text-jobs/${id}/complete`, { price: parseFloat(f.price), notes: f.notes });
      toast.success("Job saved to the CRM");
      setItems((p) => p.filter((i) => i.id !== id));
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save job");
      setForm((p) => ({ ...p, [id]: { ...f, saving: false } }));
    }
  };

  if (!items.length) return null;
  return (
    <div>
      <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Text-Scheduled Jobs</h2>
      <p className="text-xs text-zinc-600 mb-3">Jobs you scheduled by text. They're saved to the customer's CRM record only after you add the price and notes below.</p>
      <div className="space-y-3">
        {items.map((j) => {
          const f = form[j.id] || {};
          const ready = parseFloat(f.price) > 0 && (f.notes || "").trim().length > 0;
          return (
            <div key={j.id} data-testid={`text-job-${j.id}`} className="bg-[#121212] border border-yellow-500/30 rounded-xl p-4">
              <div className="text-white font-heading font-semibold">{fmtTime12(j.time)} — {j.name}</div>
              <div className="text-zinc-400 text-xs mt-0.5">{j.issue || "Mobile service"}{j.vehicle ? ` · ${j.vehicle}` : ""}</div>
              <div className="text-zinc-600 text-xs">{fmtDateOnly(j.date)}{j.address ? ` · ${j.address}` : ""}{j.quote ? ` · quoted ${j.quote}` : ""}</div>
              <div className="mt-3 space-y-2">
                <Input data-testid={`text-job-price-${j.id}`} type="number" min="0" step="0.01" inputMode="decimal" className={inputCls} placeholder="Final price ($)" value={f.price || ""} onChange={(e) => setForm((p) => ({ ...p, [j.id]: { ...f, price: e.target.value } }))} />
                <Input data-testid={`text-job-notes-${j.id}`} className={inputCls} placeholder="Work notes (required)" value={f.notes || ""} onChange={(e) => setForm((p) => ({ ...p, [j.id]: { ...f, notes: e.target.value } }))} />
                <button data-testid={`text-job-save-${j.id}`} onClick={() => save(j.id)} disabled={!ready || f.saving} className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-sm py-2.5 text-sm font-semibold">
                  {f.saving && <Loader2 className="h-4 w-4 animate-spin" />} Mark Complete & Save to CRM
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function Tech() {
  const [tech, setTech] = useState(null); // null=checking, false=guest
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeJobId, setActiveJobId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/tech/me`).then((res) => setTech(res.data)).catch(() => setTech(false));
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tech/jobs`);
      setJobs(res.data);
    } catch {
      toast.error("Could not load jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (tech) fetchJobs(); }, [tech, fetchJobs]);

  const logout = async () => {
    await axios.post(`${API}/tech/logout`).catch(() => {});
    setTech(false);
    setJobs([]);
    setActiveJobId(null);
  };

  if (tech === null) {
    return <main className="pt-20 md:pt-24 min-h-screen flex items-center justify-center" data-testid="tech-page"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></main>;
  }
  if (!tech) {
    return <main className="pt-20 md:pt-24 min-h-screen" data-testid="tech-page"><TechLogin onLogin={setTech} /></main>;
  }

  const today = new Date().toISOString().slice(0, 10);
  const todayJobs = jobs.filter((j) => j.date === today);
  const upcoming = jobs.filter((j) => j.date > today && !["Completed", "Cancelled"].includes(j.status));
  const past = jobs.filter((j) => j.date < today).slice(-10).reverse();

  const JobCard = ({ job }) => (
    <button
      data-testid={`tech-job-card-${job.appointment_id}`}
      onClick={() => setActiveJobId(job.appointment_id)}
      className="w-full text-left bg-[#121212] border border-zinc-800 hover:border-blue-500/50 rounded-xl p-4 transition-colors"
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-white font-heading font-semibold">{fmtTime12(job.start_time)} — {job.name}</div>
          <div className="text-zinc-400 text-xs mt-0.5">{job.service_type}{job.vehicle_info ? ` · ${job.vehicle_info.year || ""} ${job.vehicle_info.make || ""} ${job.vehicle_info.model || ""}` : ""}</div>
          <div className="text-zinc-600 text-xs">{fmtDateOnly(job.date)}{job.address ? ` · ${job.address}` : ""}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`text-[10px] font-bold uppercase border rounded-sm px-2 py-0.5 ${jobStatusColor[job.status] || jobStatusColor.Scheduled}`}>{job.status}</span>
          {job.priority && job.priority !== "Normal" && (
            <span className="text-[10px] font-bold uppercase bg-red-600/20 text-red-300 border border-red-500/40 rounded-sm px-2 py-0.5">{job.priority}</span>
          )}
        </div>
      </div>
    </button>
  );

  return (
    <main className="pt-20 md:pt-24 min-h-screen" data-testid="tech-page">
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-8">
        {activeJobId ? (
          <JobDetail jobId={activeJobId} tech={tech} onBack={() => { setActiveJobId(null); fetchJobs(); }} />
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-2xl font-bold text-white" data-testid="tech-greeting">Hey, {tech.name.split(" ")[0]}</h1>
                <p className="text-zinc-500 text-sm">{todayJobs.length} job{todayJobs.length === 1 ? "" : "s"} today</p>
              </div>
              <button data-testid="tech-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 border border-zinc-800 text-zinc-400 hover:text-white rounded-sm px-3 py-2 text-xs font-semibold">
                <LogOut className="h-3.5 w-3.5" /> Sign Out
              </button>
            </div>
            <div className="mb-4 flex justify-end"><NotificationBell /></div>
            {loading ? (
              <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
            ) : (
              <div className="space-y-8">
                <TextJobs onSaved={fetchJobs} />
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Today</h2>
                  {todayJobs.length === 0 ? (
                    <div className="text-zinc-600 text-sm border border-zinc-800 rounded-xl p-6 text-center" data-testid="tech-no-jobs"><Inbox className="h-8 w-8 mx-auto mb-2 text-zinc-700" />No jobs assigned today.</div>
                  ) : (
                    <div className="space-y-3">{todayJobs.map((j) => <JobCard key={j.appointment_id} job={j} />)}</div>
                  )}
                </div>
                {upcoming.length > 0 && (
                  <div>
                    <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Upcoming</h2>
                    <div className="space-y-3">{upcoming.map((j) => <JobCard key={j.appointment_id} job={j} />)}</div>
                  </div>
                )}
                {past.length > 0 && (
                  <div>
                    <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Recent</h2>
                    <div className="space-y-3">{past.map((j) => <JobCard key={j.appointment_id} job={j} />)}</div>
                  </div>
                )}
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Quick Add by Text</h2>
                  <div className="border border-zinc-800 rounded-xl p-5 bg-zinc-900/40" data-testid="tech-text-add-card">
                    <p className="text-sm text-zinc-300">Add a customer — or schedule a job on the Google Calendar — straight from your phone. Text this address:</p>
                    <p className="text-sm font-semibold text-blue-400 mt-2 select-all">garrett@masonsmobile.com</p>
                    <p className="text-xs text-zinc-500 mt-3">Start with <span className="text-zinc-300 font-mono">ADD</span> or <span className="text-zinc-300 font-mono">SCHEDULE</span>, then the customer name + phone in any order. Include a date/time to also put it on the calendar:</p>
                    <p className="text-xs text-zinc-300 font-mono mt-2 bg-zinc-950/60 rounded-md p-2.5 border border-zinc-800">SCHEDULE John Smith 7025551234 2015 Civic no start, tomorrow 2pm, 123 Main St, quoted $350</p>
                    <p className="text-xs text-zinc-500 mt-2">You'll get a text back confirming. The job is saved to the customer's CRM record only after you add the final price and notes under Text-Scheduled Jobs and mark it complete. Only works from the approved phone numbers in Admin → Settings → Booking Text Alerts.</p>
                  </div>
                </div>
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Notifications</h2>
                  <div className="space-y-4">
                    <PushSettings />
                    <NotifyPrefsMatrix categories={TECH_PREF_CATEGORIES} title="My Notification Preferences" />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
